/**
 * Profile: opt-in for sports participation; when enabled, guides users to a real society
 * (not city placeholders like "Lahore Area") using the same SocietyPickerModal flow as signup.
 * With a linked society and opt-in checked, users pick interests from Cricket, Football, Badminton, Tennis
 * (GET/POST/DELETE /api/sports/my-interests/).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SocietyPickerModal from '../register/SocietyPickerModal';
import { authService } from '../../../services/api';
import sportsService from '../../../services/sportsService';

const PLACEHOLDER_SOCIETY_NAMES_LOWER = new Set(['lahore area']);

/** Fixed pick list (order preserved in UI); must match seeded Sport names in DB. */
const PROFILE_SPORT_NAMES = ['Cricket', 'Football', 'Badminton', 'Tennis'];

function isPlaceholderSocietyName(name) {
  return PLACEHOLDER_SOCIETY_NAMES_LOWER.has((name || '').trim().toLowerCase());
}

export default function SportsInterestSection({
  userEmail,
  society,
  city,
  interestedInSports: interestedInSportsProp,
  onUpdated,
}) {
  const [interested, setInterested] = useState(Boolean(interestedInSportsProp));
  const [savingToggle, setSavingToggle] = useState(false);
  const [savingSociety, setSavingSociety] = useState(false);
  const [sectionError, setSectionError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('pick');
  const [profileSportCatalog, setProfileSportCatalog] = useState([]);
  const [mySportInterests, setMySportInterests] = useState([]);
  const [loadingProfileSports, setLoadingProfileSports] = useState(false);
  const [sportsMutation, setSportsMutation] = useState(false);
  const [sportsError, setSportsError] = useState('');

  useEffect(() => {
    setInterested(Boolean(interestedInSportsProp));
  }, [interestedInSportsProp]);

  const cityForPicker = (society?.city || city || '').trim();
  const hasRealSociety =
    society?.name && !isPlaceholderSocietyName(society.name);
  const isPlaceholderOnly =
    society?.name && isPlaceholderSocietyName(society.name);

  const nameOrderLower = useMemo(
    () => PROFILE_SPORT_NAMES.map((n) => n.toLowerCase()),
    [],
  );

  const loadProfileSportsData = useCallback(async () => {
    if (!interested || !hasRealSociety) return;
    setLoadingProfileSports(true);
    setSportsError('');
    try {
      const [allSports, interests] = await Promise.all([
        sportsService.listSports(),
        sportsService.listMyInterests(),
      ]);
      const catalog = (allSports || [])
        .filter((s) =>
          nameOrderLower.includes((s.name || '').trim().toLowerCase()),
        )
        .sort(
          (a, b) =>
            nameOrderLower.indexOf((a.name || '').trim().toLowerCase()) -
            nameOrderLower.indexOf((b.name || '').trim().toLowerCase()),
        );
      setProfileSportCatalog(catalog);
      const allowedIds = new Set(catalog.map((s) => s.id));
      setMySportInterests(
        (interests || []).filter((row) => allowedIds.has(row.sport)),
      );
    } catch (e) {
      setSportsError(e?.message || 'Could not load sports.');
      setProfileSportCatalog([]);
      setMySportInterests([]);
    } finally {
      setLoadingProfileSports(false);
    }
  }, [interested, hasRealSociety, nameOrderLower]);

  useEffect(() => {
    loadProfileSportsData();
  }, [loadProfileSportsData, society?.id]);

  const selectedSportIds = useMemo(
    () => new Set(mySportInterests.map((row) => row.sport)),
    [mySportInterests],
  );

  const sportsAvailableToAdd = useMemo(
    () => profileSportCatalog.filter((s) => !selectedSportIds.has(s.id)),
    [profileSportCatalog, selectedSportIds],
  );

  const selectedSportsOrdered = useMemo(
    () =>
      profileSportCatalog
        .filter((s) => selectedSportIds.has(s.id))
        .map((s) => ({
          id: s.id,
          name: s.name,
          icon: s.icon,
        })),
    [profileSportCatalog, selectedSportIds],
  );

  const handleAddSportFromDropdown = async (e) => {
    const raw = e.target.value;
    e.target.value = '';
    if (!raw) return;
    const sportId = parseInt(raw, 10);
    if (Number.isNaN(sportId)) return;
    setSportsError('');
    setSportsMutation(true);
    try {
      await sportsService.addInterest(sportId);
      await loadProfileSportsData();
    } catch (err) {
      setSportsError(err?.message || 'Could not add sport.');
    } finally {
      setSportsMutation(false);
    }
  };

  const handleRemoveSport = async (sportId) => {
    setSportsError('');
    setSportsMutation(true);
    try {
      await sportsService.removeInterest(sportId);
      await loadProfileSportsData();
    } catch (err) {
      setSportsError(err?.message || 'Could not remove sport.');
    } finally {
      setSportsMutation(false);
    }
  };

  const persistInterested = useCallback(async (next) => {
    setSavingToggle(true);
    setSectionError('');
    try {
      await authService.updateUserProfile({ interested_in_sports: next });
      setInterested(next);
      if (onUpdated) onUpdated();
    } catch (e) {
      setSectionError(e?.message || 'Could not update preference.');
    } finally {
      setSavingToggle(false);
    }
  }, [onUpdated]);

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    if (!checked) {
      persistInterested(false);
      return;
    }
    persistInterested(true);
  };

  const applySocietyId = async (societyId) => {
    const sid = parseInt(String(societyId), 10);
    if (societyId == null || String(societyId).trim() === '' || Number.isNaN(sid)) {
      setSectionError('Invalid society selection.');
      return;
    }
    setSavingSociety(true);
    setSectionError('');
    try {
      await authService.updateUserProfile({ society: sid });
      setModalOpen(false);
      if (onUpdated) onUpdated();
    } catch (e) {
      setSectionError(e?.message || 'Could not update society.');
    } finally {
      setSavingSociety(false);
    }
  };

  return (
    <div className="bg-gray-50 p-6 rounded-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Sports and community</h3>

      <label className="flex items-start gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={interested}
          onChange={handleCheckboxChange}
          disabled={savingToggle}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-800">
          I want to take part in sports
        </span>
      </label>

      {sectionError && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {sectionError}
        </p>
      )}

      {interested && (
        <div className="mt-5 border border-gray-200 rounded-lg bg-white p-4 space-y-4">
          <p className="text-sm text-gray-600">
            Local sports activities are organized by society. Use your real residential society so teams and venues match your area.
          </p>

          {!cityForPicker && (
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3">
              Add your <strong>city</strong> in Basic Information above, then choose or add your society here.
            </div>
          )}

          {!!cityForPicker && !society && (
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3">
              You have not selected a society yet. Please choose your society, or add it if it is not listed.
            </div>
          )}

          {!!cityForPicker && isPlaceholderOnly && (
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm p-3">
              Your account is still on the city placeholder society <strong>{society.name}</strong>.
              Please select your actual residential society to take part in sports.
            </div>
          )}

          {!!cityForPicker && hasRealSociety && (
            <>
              <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-md p-3">
                Linked to <strong>{society.name}</strong>
                {society.city ? ` (${society.city})` : ''}.
              </p>
              {interested && (
                <div className="mt-4 space-y-4 rounded-lg border border-emerald-100 bg-white p-4">
                  <div>
                    <label
                      htmlFor="profile-sport-add"
                      className="block text-sm font-medium text-gray-900 mb-1.5"
                    >
                      Select sports you are interested in
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Choose one at a time from the list (Cricket, Football, Badminton, Tennis).
                    </p>
                    <select
                      id="profile-sport-add"
                      defaultValue=""
                      disabled={
                        loadingProfileSports ||
                        sportsMutation ||
                        sportsAvailableToAdd.length === 0
                      }
                      onChange={handleAddSportFromDropdown}
                      className="w-full max-w-md rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <option value="">
                        {loadingProfileSports
                          ? 'Loading sports…'
                          : sportsAvailableToAdd.length === 0
                            ? 'All listed sports are selected'
                            : 'Add a sport…'}
                      </option>
                      {sportsAvailableToAdd.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.icon ? `${s.icon} ` : ''}
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {sportsError && (
                    <p className="text-sm text-red-600" role="alert">
                      {sportsError}
                    </p>
                  )}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      Your selected sports
                    </h4>
                    {loadingProfileSports && mySportInterests.length === 0 ? (
                      <p className="mt-2 text-sm text-gray-500">Loading…</p>
                    ) : selectedSportsOrdered.length === 0 ? (
                      <p className="mt-2 text-sm text-gray-500">
                        None selected yet. Use the dropdown above to add sports.
                      </p>
                    ) : (
                      <ul className="mt-2 flex flex-wrap gap-2 list-none p-0">
                        {selectedSportsOrdered.map(({ id, name, icon }) => (
                          <li
                            key={id}
                            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-900"
                          >
                            <span>
                              {icon ? `${icon} ` : ''}
                              {name}
                            </span>
                            <button
                              type="button"
                              disabled={sportsMutation}
                              onClick={() => handleRemoveSport(id)}
                              className="rounded-full p-0.5 text-emerald-700 hover:bg-emerald-200/80 disabled:opacity-50"
                              aria-label={`Remove ${name}`}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {!!cityForPicker && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={savingSociety}
                onClick={() => {
                  setModalMode('pick');
                  setModalOpen(true);
                }}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
              >
                {hasRealSociety ? 'Change society' : 'Select society'}
              </button>
              <button
                type="button"
                disabled={savingSociety}
                onClick={() => {
                  setModalMode('add');
                  setModalOpen(true);
                }}
                className="px-4 py-2 rounded-md border border-blue-200 bg-blue-50 text-blue-800 text-sm font-medium hover:bg-blue-100 disabled:opacity-60"
              >
                My society isn&apos;t listed — add society
              </button>
            </div>
          )}
        </div>
      )}

      <SocietyPickerModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setModalMode('pick');
        }}
        city={cityForPicker}
        requesterEmail={userEmail || ''}
        maxPanelHeightPx={360}
        initialMode={modalMode}
        onSelect={(societyId) => applySocietyId(societyId)}
      />
    </div>
  );
}
